import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ScrollText, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { superAdminAPI } from '../../services/api';

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (actionFilter) params.action = actionFilter;
      const response = await superAdminAPI.getAuditLog(params);
      const result = response.data?.data || response.data;
      setLogs(Array.isArray(result) ? result : result.items || []);
      setTotalPages(result.totalPages || result.pages || 1);
    } catch (error) {
      toast.error('فشل في تحميل سجل العمليات');
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    setPage(1);
  }, [actionFilter]);

  const ACTION_OPTIONS = [
    { value: '', label: 'كل العمليات' },
    { value: 'login', label: 'تسجيل دخول' },
    { value: 'create_tenant', label: 'إنشاء مختبر' },
    { value: 'update_tenant', label: 'تحديث مختبر' },
    { value: 'toggle_status', label: 'تغيير حالة' },
    { value: 'create_plan', label: 'إنشاء باقة' },
    { value: 'update_plan', label: 'تحديث باقة' },
    { value: 'update_subscription', label: 'تحديث اشتراك' },
    { value: 'record_payment', label: 'تسجيل دفعة' },
    { value: 'impersonate', label: 'انتحال هوية' },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">سجل العمليات</h1>
          <p className="text-sm text-gray-500 mt-1">جميع العمليات الإدارية</p>
        </div>
      </div>

      {/* Filter */}
      <div className="mb-6">
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
        >
          {ACTION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-right px-6 py-3 font-medium text-gray-500">الأدمن</th>
                <th className="text-right px-6 py-3 font-medium text-gray-500">العملية</th>
                <th className="text-right px-6 py-3 font-medium text-gray-500 hidden md:table-cell">الهدف</th>
                <th className="text-right px-6 py-3 font-medium text-gray-500">التاريخ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4"><div className="h-4 w-24 bg-gray-200 rounded" /></td>
                    <td className="px-6 py-4"><div className="h-4 w-28 bg-gray-200 rounded" /></td>
                    <td className="px-6 py-4 hidden md:table-cell"><div className="h-4 w-32 bg-gray-200 rounded" /></td>
                    <td className="px-6 py-4"><div className="h-4 w-20 bg-gray-200 rounded" /></td>
                  </tr>
                ))
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-12 text-gray-400">
                    <ScrollText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    لا توجد عمليات
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <motion.tr
                    key={log._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 font-medium text-gray-800">
                      {log.adminName || log.admin?.name || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                        {log.action || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600 hidden md:table-cell">
                      {log.targetName || log.target || log.details || '-'}
                    </td>
                    <td className="px-6 py-4 text-gray-500 text-xs">
                      {log.createdAt
                        ? new Date(log.createdAt).toLocaleDateString('ar-SA-u-ca-gregory') + ' ' +
                          new Date(log.createdAt).toLocaleTimeString('ar-SA-u-ca-gregory', { hour: '2-digit', minute: '2-digit' })
                        : '-'}
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100">
            <p className="text-sm text-gray-500">صفحة {page} من {totalPages}</p>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40">
                <ChevronRight className="w-4 h-4" />
              </button>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40">
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLog;
